"use client";

import { c as _c } from "react/compiler-runtime";
import { type ReactElement, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { ensureStableSeizeLink } from "@/helpers/SeizeLinkParser";
import { createLinkHandlers } from "@/components/drops/view/part/dropPartMarkdown/handlers";
import type { LinkHandler } from "@/components/drops/view/part/dropPartMarkdown/linkTypes";
import { parseUrl, shouldUseOpenGraphPreview } from "@/components/drops/view/part/dropPartMarkdown/linkUtils";
import LinkPreviewCard from "./LinkPreviewCard";
import { LinkPreviewProvider, useLinkPreviewContext, type LinkPreviewVariant } from "./LinkPreviewContext";
interface SmartLinkPreviewProps {
  readonly href: string;
  readonly variant?: LinkPreviewVariant | undefined;
  readonly renderFallback?: (() => ReactElement) | undefined;
}
const getFallbackLabel = (href: string): string => {
  try {
    const url = new URL(href);
    const host = url.hostname.replace(/^www\./i, "");
    return host || href;
  } catch {
    return href;
  }
};
const DefaultFallback = ({
  href
}: {
  readonly href: string;
}) => {
  let isHttpUrl = false;
  try {
    const parsed = new URL(href);
    isHttpUrl = parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    isHttpUrl = false;
  }
  if (!isHttpUrl) {
    return <span className="tw-[overflow-wrap:anywhere] tw-break-words tw-text-sm tw-font-semibold tw-text-iron-100">
        {getFallbackLabel(href)}
      </span>;
  }
  return <a href={href} target="_blank" rel="noopener noreferrer nofollow" className="tw-[overflow-wrap:anywhere] tw-break-words tw-text-sm tw-font-semibold tw-text-iron-100 tw-no-underline tw-transition tw-duration-200 hover:tw-text-white">
      {getFallbackLabel(href)}
    </a>;
};
const findMatch = (handlers: readonly LinkHandler[], href: string): LinkHandler | null => {
  for (const handler of handlers) {
    if (handler.match(href)) {
      return handler;
    }
  }
  return null;
};
export default function SmartLinkPreview(t0) {
  const $ = _c(9);
  const {
    href,
    variant,
    renderFallback
  } = t0;
  const {
    variant: contextVariant
  } = useLinkPreviewContext();
  const resolvedVariant = variant ?? contextVariant;
  let content;
  if ($[0] !== href || $[1] !== renderFallback || $[2] !== resolvedVariant) {
    const stableHref = ensureStableSeizeLink(href);
    let t1;
    if ($[4] !== resolvedVariant) {
      t1 = createLinkHandlers({
        linkPreviewVariant: resolvedVariant
      });
      $[4] = resolvedVariant;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    const handlers = t1;
    const parsedUrl = parseUrl(stableHref);
    const fallbackRenderer = renderFallback ?? (() => <DefaultFallback href={stableHref} />);
    const renderOpenGraph = () => {
      if (!shouldUseOpenGraphPreview(stableHref, parsedUrl)) {
        return fallbackRenderer();
      }
      return <LinkPreviewCard href={stableHref} variant={resolvedVariant} renderFallback={fallbackRenderer} />;
    };
    const renderHandlerContent = element => <ErrorBoundary fallbackRender={() => renderOpenGraph()}>{element}</ErrorBoundary>;
    const renderFromHandler = handler => {
      const rendered = handler.render(stableHref);
      return renderHandlerContent(rendered);
    };
    const handler_0 = findMatch(handlers, stableHref);
    if (handler_0) {
      try {
        content = renderFromHandler(handler_0);
      } catch {
        content = renderOpenGraph();
      }
    } else {
      content = renderOpenGraph();
    }
    $[0] = href;
    $[1] = renderFallback;
    $[2] = resolvedVariant;
    $[3] = content;
  } else {
    content = $[3];
  }
  let t1;
  if ($[6] !== content || $[7] !== resolvedVariant) {
    t1 = <LinkPreviewProvider variant={resolvedVariant}>{content}</LinkPreviewProvider>;
    $[6] = content;
    $[7] = resolvedVariant;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}